import type { Rng } from '@/engine/ports';

// LLM thinking delay (spec §4.4 L240): 2.5–6s for offline-ish feel.
const LLM_MIN_MS = 2_500;
const LLM_MAX_MS = 6_000;

// Bot action pacing — bumped from 1.5-2.5s after V2-prep playtest:
// the prior band felt rushed once the thinking bubble was made
// prominent. 2.5-4.5s gives a visible "they're thinking" beat before
// each action, while still finishing a hand in ~30-60s.
const BOT_MIN_MS = 2_500;
const BOT_MAX_MS = 4_500;

// Quick preflop-fold pacing — early-street folds don't deserve the full
// "they're thinking" beat since there's no information to digest yet.
// User feedback said preflop fold delays felt long; 600-1200ms is fast
// enough to keep the hand moving but still visually registers as "bot
// acted" rather than an instant skip.
const BOT_QUICK_FOLD_MIN_MS = 600;
const BOT_QUICK_FOLD_MAX_MS = 1_200;

function inTestEnv(): boolean {
  if (typeof process !== 'undefined' && process.env?.VITEST === 'true') return true;
  if (typeof globalThis !== 'undefined' && '__POKER_FAST_PLAY__' in globalThis) return true;
  return false;
}

/**
 * Cosmetic "thinking" delay for LLM agents (spec §4.4 L240). Sampled
 * uniformly from [1.5s, 6s]; short-circuits to 0 in tests.
 */
export function delayThinking(rng: Rng): Promise<void> {
  if (inTestEnv()) return Promise.resolve();
  const range = LLM_MAX_MS - LLM_MIN_MS;
  const ms = LLM_MIN_MS + Math.floor(rng.next() * range);
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Bot turn pacing for the live /play loop. Shorter than `delayThinking`
 * so a full hand still takes ~10-20s rather than minutes, but long
 * enough that each action is visually distinct. Test runners bypass via
 * `__POKER_FAST_PLAY__` (Playwright sets it) or `VITEST=true`.
 */
export function delayBotAction(rng: Rng): Promise<void> {
  if (inTestEnv()) return Promise.resolve();
  const range = BOT_MAX_MS - BOT_MIN_MS;
  const ms = BOT_MIN_MS + Math.floor(rng.next() * range);
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Short cosmetic delay for preflop folds. Used when the bot has decided
 * to fold before any community cards — no reason to linger on the
 * thinking bubble for several seconds. Short-circuits to 0 in tests.
 */
export function delayBotQuickFold(rng: Rng): Promise<void> {
  if (inTestEnv()) return Promise.resolve();
  const range = BOT_QUICK_FOLD_MAX_MS - BOT_QUICK_FOLD_MIN_MS;
  const ms = BOT_QUICK_FOLD_MIN_MS + Math.floor(rng.next() * range);
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Fixed-duration pause (used for street advance / hand-end). */
export function pauseMs(ms: number): Promise<void> {
  if (inTestEnv()) return Promise.resolve();
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export {
  BOT_MAX_MS as BOT_ACTION_MAX_MS,
  BOT_MIN_MS as BOT_ACTION_MIN_MS,
  BOT_QUICK_FOLD_MAX_MS,
  BOT_QUICK_FOLD_MIN_MS,
  LLM_MAX_MS as THINKING_MAX_MS,
  LLM_MIN_MS as THINKING_MIN_MS,
};
